import * as React from 'react'
import { FormEvent, ReactElement, useContext, useEffect, useMemo, useRef } from 'react'
import { CommandBar, CommandBarButton, ICommandBarItemProps, Separator, Stack, StackItem, Text, TextField } from '@fluentui/react'
import { IUseBooleanCallbacks, useBoolean } from '@fluentui/react-hooks'
import nl2br from 'react-nl2br'
import { formatFullDateTime } from '../../helpers/dateHelper'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, Store } from '../../reducers'
import { ShortUserListItemDto } from '../../config/userConfig'
import { NoteAction, NoteMutationDto, NoteOverviewDto } from '../../config/noteConfig'
import { Formik, FormikContextType, FormikHelpers, useFormikContext } from 'formik'
import * as Yup from 'yup'
import Loading from '../../components/Loading'
import { IFileDto } from '../../config/fileConfig'
import useUser from '../../hooks/useUser'
import useAxios from '../../hooks/useAxios'
import { AxiosInstance } from 'axios'
import DialogButton from '../../components/Button/DialogButton'
import DeleteDialog, { DeleteDialogProps } from '../../components/Dialog/LegacyDeleteDialog'
import FormattedDateTime from '../Date/FormattedDateTime'
import { useTranslation } from 'react-i18next'
import PersonAvatar from '../PersonAvatar'
import useLocalStorage, { LocalStorageResult } from '../../hooks/useLocalStorage'
import { MessageBar, MessageBarBody } from '@fluentui/react-components'
import AuthorizationContext from '../../context/AuthorizationContext'
import Tooltip from '../Tooltip'

type Props = NoteOverviewDto & {
    file?: IFileDto
    reset: () => any
}

type ContainerProps = {
    note: NoteOverviewDto
    editProps: [boolean, IUseBooleanCallbacks]
    reset: () => any
    draft?: LocalStorageResult<string>
}

type ContentProps = {
    edit: boolean
    setEdit: () => void
    savedContent?: string
    updateDraft?: (value: string) => void
}

type RevisionTitleProps = {
    isNew: boolean
    author: ShortUserListItemDto
    date: string
}

type CommandBarItem = ICommandBarItemProps & { visible: boolean; tooltip?: string }
type CommandBarItems = {
    items: CommandBarItem[]
    farItems: CommandBarItem[]
}

/**
 * Renders the command bar button inside a Tooltip that explains the icon. The title moves to the
 * aria-label so the native browser tooltip doesn't show up next to the Fluent one.
 */
const withTooltip = (item: CommandBarItem): CommandBarItem => {
    if (item.onRender) {
        return item
    }
    const { tooltip, title, ...rest } = item
    return {
        ...rest,
        ariaLabel: item.ariaLabel || title,
        commandBarButtonAs: buttonProps => {
            const { defaultRender: DefaultRender } = buttonProps
            return DefaultRender ? (
                <Tooltip hostProps={{ content: tooltip || title }}>
                    <DefaultRender {...buttonProps} />
                </Tooltip>
            ) : null
        },
    }
}

const validationSchema = Yup.object().shape({
    content: Yup.string(),
})

const getCommandBarItems = (
    context: FormikContextType<NoteMutationDto>,
    dispatch: Dispatch,
    axios: AxiosInstance,
    translate: (key: string, options?: any) => string,
    { note, editProps: [edit, { setTrue: setEdit, setFalse: unsetEdit }], reset, draft }: ContainerProps,
    isAuthor: boolean,
): CommandBarItems => ({
    items: [
        {
            visible: edit,
            key: 'cancel-note',
            iconProps: { iconName: 'Cancel' },
            iconOnly: true,
            title: translate('action.cancel'),
            tooltip: translate('file.pivot.note.tooltip.cancel'),
            disabled: context.isSubmitting,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                context.setFieldValue('content', note.content)
                draft?.setValues(null)
                unsetEdit()
            },
        },
        {
            visible: note.id && !edit,
            key: 'delete-note',
            title: translate('action.delete'),
            onRender: () => (
                <DialogButton<DeleteDialogProps>
                    button={CommandBarButton}
                    buttonProps={{
                        styles: { root: { height: '100%' } },
                        iconProps: { iconName: 'Delete' },
                        disabled: context.isSubmitting,
                        onMouseDown: event => event.preventDefault(),
                    }}
                    dialog={DeleteDialog}
                    tooltipProps={{ content: translate('file.pivot.note.tooltip.delete') }}
                    dialogProps={{
                        dialogContentProps: {
                            title: translate('file.pivot.note.delete.title'),
                            subText: translate('file.pivot.note.delete.subtitle'),
                        },
                        onDelete: () => {
                            context.setSubmitting(true)
                            draft?.setValues(null)
                            dispatch({
                                type: NoteAction.Delete,
                                payload: axios.delete(`/note/${note.id}`).then(() => reset()),
                            })
                        },
                    }}
                />
            ),
        },
        {
            visible: !!note.id,
            key: 'private-note',
            iconProps: { iconName: context.values.isPrivate ? 'Lock' : 'Unlock' },
            isIconOnly: true,
            title: context.values.isPrivate ? translate('file.pivot.note.private.title') : translate('file.pivot.note.public.title'),
            tooltip: context.values.isPrivate ? translate('file.pivot.note.tooltip.private') : translate('file.pivot.note.tooltip.public'),
            disabled: context.isSubmitting || !isAuthor,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                ;(async () =>
                    context.setFieldValue(
                        'isPrivate',
                        note.id ? (await axios.post(`/note/${note.id}/toggle-private`)).data : !context.values.isPrivate,
                    ))()
            },
        },
        {
            visible: !!note.id,
            key: 'note-revisions',
            iconProps: { iconName: 'Rotate' },
            text: (note.revisionCount || 0).toString(),
            title: translate('file.pivot.note.revision.count', { count: note.revisionCount }),
            tooltip: translate('file.pivot.note.tooltip.revisions', { count: note.revisionCount }),
        },
    ]
        .filter(item => item.visible)
        .map(withTooltip),
    farItems: [
        {
            // Reverts the unsaved draft back to the stored note, while staying in edit mode.
            visible: edit && !!note.id && !!draft?.values && draft.values !== note.content,
            key: 'undo-note',
            iconProps: { iconName: 'Undo' },
            iconOnly: true,
            title: translate('action.undo'),
            tooltip: translate('file.pivot.note.tooltip.undo'),
            disabled: context.isSubmitting,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                context.setFieldValue('content', note.content)
                draft?.setValues(null)
            },
        },
        {
            visible: edit,
            key: 'save-note',
            iconProps: { iconName: 'Save' },
            iconOnly: true,
            title: translate('action.save'),
            tooltip: translate('file.pivot.note.tooltip.save'),
            disabled: context.isSubmitting,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                context.submitForm()
            },
        },
        {
            visible: !edit,
            key: 'edit-note',
            iconProps: { iconName: 'Edit' },
            iconOnly: true,
            title: translate('action.edit'),
            tooltip: translate('file.pivot.note.tooltip.edit'),
            onClick: setEdit,
        },
    ]
        .filter(item => item.visible)
        .map(withTooltip),
})

export function RevisionTitle(props: RevisionTitleProps): ReactElement {
    const { currentUser } = useUser()
    const { t: translate } = useTranslation()

    const author = props.author ? (props.author?.id === currentUser?.id ? 'uzelf' : props.author.fullName) : 'onbekend'

    return (
        <Separator styles={{ root: { flex: 1 } }}>
            {props.isNew ? (
                <Text variant="small">{translate('file.pivot.note.revision.title.new')}</Text>
            ) : (
                <>
                    <FormattedDateTime date={props.date || new Date()} />
                    <Text variant="small">{translate('file.pivot.note.revision.title.saved', { author: author })} </Text>
                </>
            )}
        </Separator>
    )
}

export function Content({ edit, setEdit, savedContent, updateDraft }: ContentProps): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue, isSubmitting } = useFormikContext<NoteMutationDto>()

    if (isSubmitting) {
        return <Loading label={translate('base.saving')} />
    } else if (edit) {
        return (
            <TextField
                value={values.content}
                onChange={(_event: FormEvent, newValue?: string) => {
                    setFieldValue('content', newValue)
                    updateDraft?.(newValue)
                }}
                styles={{
                    root: { margin: '-7px -1px 0px -1px' },
                    field: { lineHeight: 'normal' },
                }}
                multiline
                autoFocus
                resizable={false}
                rows={10}
            />
        )
    }

    return (
        <Text
            variant="medium"
            onClick={setEdit}
            styles={{
                root: {
                    height: '100%',
                    overflow: 'auto',
                    padding: '6px 8px 5px 8px',
                    margin: '-6px 0 1px 0',
                },
            }}
        >
            {/* View mode always shows the stored note; an unsaved draft only lives in the editor. */}
            {nl2br(savedContent ?? values.content)}
        </Text>
    )
}

function Container(props: ContainerProps): ReactElement {
    const axios = useAxios()
    const dispatch: Dispatch = useDispatch()
    const { currentUser } = useUser()
    const { t: translate } = useTranslation()

    const context = useFormikContext<NoteMutationDto>()
    const {
        note,
        editProps: [edit, { setTrue: setEdit }],
        draft,
    } = props

    useEffect(() => {
        // Restore an unsaved draft once the LocalStorage value is available. Saved notes stay in
        // view mode (the unsaved-change notice reveals the draft); the new-note editor opens for writing.
        if (draft?.loaded && draft.values && draft.values !== note.content) {
            context.setFieldValue('content', draft.values)
            if (!note.id) {
                setEdit()
            }
        }
    }, [draft?.loaded])

    const hasUnsavedChange = !!note.id && !!draft?.values && draft.values !== note.content

    const isAuthor = useMemo<boolean>(() => note.createdByAuthor?.id === currentUser?.id, [note, currentUser])
    const commandBarItems = useMemo<CommandBarItems>(
        () => getCommandBarItems(context, dispatch, axios, translate, props, isAuthor),
        [context.values, props, isAuthor],
    )

    return (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 240, maxWidth: 480 } }}>
            <StackItem>
                <PersonAvatar size={28} name={note.id ? note.createdByAuthor.fullName : currentUser?.fullName} color={'colorful'} />{' '}
                <Text variant={'small'}>
                    {note.id &&
                        translate('file.pivot.note.subtitle', {
                            datetime: formatFullDateTime(note.createdAt),
                        })}
                </Text>
                <RevisionTitle isNew={!note.id} date={note.updatedAt} author={note.updatedByAuthor} />
            </StackItem>
            <Stack verticalFill styles={{ root: { minHeight: 'calc(10em + 17px)', fontSize: 18 } }}>
                <Content
                    edit={edit}
                    setEdit={setEdit}
                    savedContent={note.id ? note.content : undefined}
                    updateDraft={value => draft?.setValues(value === note.content ? null : value)}
                />
            </Stack>
            {!edit && hasUnsavedChange && (
                <StackItem>
                    <MessageBar intent={'warning'}>
                        <MessageBarBody>{translate('file.pivot.note.unsaved')}</MessageBarBody>
                    </MessageBar>
                </StackItem>
            )}
            <StackItem styles={{ root: { marginTop: '1px !important' } }}>
                <CommandBar styles={{ root: { padding: 0 } }} items={commandBarItems.items} farItems={commandBarItems.farItems} />
            </StackItem>
        </Stack>
    )
}

export default function Note(props: Props): ReactElement {
    const { host } = useContext(AuthorizationContext)
    const axios = useAxios()
    const editProps = useBoolean(!props.id)
    const dispatch: Dispatch = useDispatch()
    const { currentUser } = useUser()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)
    // Guards against overlapping submits (e.g. a double-click on save) creating duplicate notes.
    const submitLockRef = useRef<boolean>(false)
    // Every note keeps its unsaved changes as a draft in LocalStorage, so a page refresh loses nothing.
    const draft = useLocalStorage<string>({
        key: `HammockNoteDraft-${currentFile?.id || props.file?.id || 'global'}-${props.id || 'new'}`,
        hostKey: host?.RowKey,
        defaultValue: '',
        fromJson: (json: string) => json,
        toJson: (values: string) => values,
    })

    return (
        <Formik<NoteMutationDto>
            initialValues={{
                content: props.content,
                authorId: currentUser?.id,
                fileId: currentFile?.id || props.file?.id,
                isPrivate: props.isPrivate,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={(values: NoteMutationDto, formikHelpers: FormikHelpers<NoteMutationDto>) => {
                if (submitLockRef.current || values.content === props.content) {
                    if (!submitLockRef.current) {
                        // The content matches the stored note, so there is no unsaved change left.
                        draft.setValues(null)
                        if (props.id) {
                            editProps[1].setFalse()
                        }
                    }
                    formikHelpers.setSubmitting(false)
                    return undefined
                }

                submitLockRef.current = true
                return dispatch(
                    props.id
                        ? {
                              type: NoteAction.Update,
                              payload: axios
                                  .put(`/note/${props.id}`, values)
                                  .then(() => props.reset())
                                  .then(() => {
                                      draft.setValues(null)
                                      editProps[1].setFalse()
                                  })
                                  .finally(() => (submitLockRef.current = false)),
                          }
                        : {
                              type: NoteAction.Add,
                              payload: axios
                                  .post('/note', values)
                                  .then(() => props.reset())
                                  .then(() => {
                                      // The saved note now appears as its own card; return the editor to a
                                      // pristine state, ready for the next note.
                                      draft.setValues(null)
                                      formikHelpers.resetForm({ values: { ...values, content: '', isPrivate: false } })
                                  })
                                  .finally(() => (submitLockRef.current = false)),
                          },
                )
            }}
        >
            <Container note={props} editProps={editProps} reset={props.reset} draft={draft} />
        </Formik>
    )
}
